import { isNotNilOrEmpty } from "client/Misc/utils";
import { AttributeSpec } from "prosemirror-model";
import { combineStyles } from "./utils";

type SupportedStyleKey = keyof Omit<
	CSSStyleDeclaration,
	| "length"
	| "parentRule"
	| "getPropertyPriority"
	| "getPropertyValue"
	| "item"
	| "removeProperty"
	| "setProperty"
	| typeof Symbol.iterator
>;

export type StyleAttributes<TStyleKey extends SupportedStyleKey> = {
	[key in TStyleKey]: string;
};

export type StyleAttributeDefinitions<TStyleKey extends SupportedStyleKey> = {
	[key in TStyleKey]: AttributeSpec;
};

export type StyleAttributeHandlers<TStyleKey extends SupportedStyleKey> = {
	styleAttributeDefinitions: StyleAttributeDefinitions<TStyleKey>;
	keys: readonly TStyleKey[];
	getStyleAttributes: (element: HTMLElement) => StyleAttributes<TStyleKey>;
	setStyleAttributes: (attrs: StyleAttributes<TStyleKey>) => string | undefined;
	applyStyleAttributesToElement: (element: HTMLElement, attrs: StyleAttributes<TStyleKey>) => void;
};

export function createStyleAttributeHandlers<TStyleKey extends SupportedStyleKey>(
	styles: { [key in TStyleKey]: string; }
): StyleAttributeHandlers<TStyleKey> {
	const keysToCopy: TStyleKey[] = Object.keys(styles) as TStyleKey[];

	const styleAttributeDefinitions = {} as StyleAttributeDefinitions<TStyleKey>;
	for (const key of keysToCopy) {
		styleAttributeDefinitions[key] = { default: undefined };
	};

	function getStyleAttributes(element: HTMLElement): StyleAttributes<TStyleKey> {
		const styleAttributes = {} as StyleAttributes<TStyleKey>;

		for (const key of keysToCopy) {
			styleAttributes[key] = readCssValue(element, key) ?? "";
		}

		return styleAttributes;
	}

	function setStyleAttributes(attrs: StyleAttributes<TStyleKey>): string | undefined {
		const styleValue: string | undefined = combineStyles(
			...keysToCopy.map(key => ({
				[styles[key]]: attrs[key]
			}))
		);

		return styleValue;
	}

	function applyStyleAttributesToElement(
		element: HTMLElement,
		attrs: StyleAttributes<TStyleKey>
	): void {
		for (const key of keysToCopy) {
			element.style
				.setProperty(styles[key], attrs[key]);
		}
	}

	return {
		keys: keysToCopy,
		getStyleAttributes,
		setStyleAttributes,
		styleAttributeDefinitions,
		applyStyleAttributesToElement
	};
}

function readCssValue(element: HTMLElement, key: SupportedStyleKey): string | undefined {
	const value: string = element.style[key];

	if (isNotNilOrEmpty(value)) {
		return value;
	}

	return tryReadCssValueFromHtml(element, key);
}

function tryReadCssValueFromHtml(element: HTMLElement, key: SupportedStyleKey): string | undefined {
	switch (key) {
		case "width":
			const width: string | null = element.getAttribute("width");
			if (!width) {
				return undefined;
			}

			if (isNaN(Number(width))) {
				return width;
			}

			return width + "px";
		case "height":
			const height: string | null = element.getAttribute("height");
			if (!height) {
				return undefined;
			}

			if (isNaN(Number(height))) {
				return height;
			}

			return height + "px";
		case "backgroundColor":
			const bgcolor: string | null = element.getAttribute("bgcolor");

			if (!bgcolor) {
				return undefined;
			}

			return bgcolor;
		default:
			return undefined;
	}
}